import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { authService } from '@/services/authService';
import { Button } from '@/components/ui/Button';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

export function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [email, setEmail] = useState('');
  const [resendStatus, setResendStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [resendError, setResendError] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage('No verification token provided.');
      return;
    }

    authService.verifyEmail(token)
      .then(() => setStatus('success'))
      .catch((err) => {
        setStatus('error');
        setErrorMessage(err instanceof Error ? err.message : 'Email verification failed');
      });
  }, [token]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground">Verifying your email...</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <div className="w-full max-w-md space-y-6 text-center">
          <div className="flex justify-center">
            <div className="rounded-full bg-green-100 dark:bg-green-900/30 p-3">
              <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-foreground">Email Verified</h2>
          <p className="text-muted-foreground">
            Your email address has been successfully verified.
          </p>
          <Button onClick={() => navigate('/')} className="w-full">
            Continue to GodMode
          </Button>
        </div>
      </div>
    );
  }

  const handleResend = async () => {
    if (!email.trim()) return;
    setResendStatus('loading');
    setResendError('');
    try {
      await authService.resendVerificationByEmail(email.trim());
      setResendStatus('success');
    } catch (err) {
      setResendStatus('error');
      setResendError(err instanceof Error ? err.message : 'Failed to resend');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <div className="w-full max-w-md space-y-6 text-center">
        <div className="flex justify-center">
          <div className="rounded-full bg-red-100 dark:bg-red-900/30 p-3">
            <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-foreground">Verification Failed</h2>
        <p className="text-muted-foreground">
          {errorMessage || 'The verification link is invalid or has already been used.'}
        </p>

        <div className="border border-border rounded-lg p-4 space-y-3 text-left">
          <p className="text-sm text-muted-foreground">
            Enter your email to receive a new verification link:
          </p>
          {resendStatus === 'success' ? (
            <p className="text-sm text-green-600 dark:text-green-400 font-medium">
              If an account exists with that email, a verification link has been sent.
            </p>
          ) : (
            <>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleResend()}
                  placeholder="your@email.com"
                  className="flex-1 px-3 py-2 text-sm rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <Button
                  onClick={handleResend}
                  disabled={resendStatus === 'loading' || !email.trim()}
                >
                  {resendStatus === 'loading' ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Resend'
                  )}
                </Button>
              </div>
              {resendStatus === 'error' && (
                <p className="text-sm text-red-600 dark:text-red-400">{resendError}</p>
              )}
            </>
          )}
        </div>

        <Button onClick={() => navigate('/')} variant="outline" className="w-full">
          Go to Home
        </Button>
      </div>
    </div>
  );
}
